import { memo } from 'react';
import I18n from 'I18n';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Small } from 'foundations-components/transitional/components/Small';
import { Link } from 'foundations-components/transitional/components/Link';
import { TruncateString } from 'foundations-components/transitional/components/TruncateString';
import { StatusTag } from 'foundations-components/transitional/components/StatusTag';
// @ts-expect-error Untyped module
import KnowledgeBaseButton from 'ui-addon-i18n/components/KnowledgeBaseButton';
import { ContentStatusToUIStatusTagMap, getContentTypeId, getIsAdvancedPageTemplate } from 'ContentEditorUI/utils/contentManagementPanel/contentTableUtils';
import { HelpIcon } from 'foundations-components/transitional/components/HelpIcon';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import Categories from 'ContentUtils/constants/Categories';
import { WarningIcon } from 'foundations-assets';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
function getShouldShowFromNowDate(ts) {
  const startOfDay = I18n.moment.portalTz().startOf('day');
  return I18n.moment.portalTz(ts).isAfter(startOfDay);
}
const getDateTime = (ts, shouldShowFromNow) => {
  return shouldShowFromNow ? I18n.moment.portalTz(ts).fromNow() : I18n.moment.portalTz(ts).format('lll');
};
export const DateCell = /*#__PURE__*/memo(({
  cellType,
  contentName,
  date
}) => {
  if (!date) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "contentManagementPanel.table.tableCellEmptyValues.date"
    });
  }
  const shouldShowFromNowDate = getShouldShowFromNowDate(date);
  const formattedDate = shouldShowFromNowDate ? I18n.moment.portalTz(date).fromNow() : I18n.moment.portalTz(date).format('ll');
  const formattedTime = shouldShowFromNowDate ? null : I18n.moment.portalTz(date).format('LT');
  const ariaLabelKey = `contentManagementPanel.table.accessibility.dateAndTime.${cellType}${shouldShowFromNowDate ? 'Ago' : ''}`;
  const ariaLabel = I18n.text(ariaLabelKey, {
    date: formattedDate,
    name: contentName,
    time: formattedTime
  });
  return /*#__PURE__*/_jsx("td", {
    children: /*#__PURE__*/_jsxs("div", {
      "aria-label": ariaLabel,
      children: [/*#__PURE__*/_jsx(TruncateString, {
        "aria-hidden": true,
        children: formattedDate
      }), /*#__PURE__*/_jsx(Small, {
        use: "help",
        "aria-hidden": true,
        children: formattedTime
      })]
    })
  });
});
const getStatusLabelKey = (status, postfix) => {
  if (postfix) {
    return `contentManagementPanel.table.statusLabels.${status}.${postfix}`;
  }
  return `contentManagementPanel.table.statusLabels.${status}`;
};
export const ContentStatus = /*#__PURE__*/memo(({
  content,
  contentStatusType,
  hasAdvancedLandingPagesWriteScope
}) => {
  const {
    securityState,
    publishDate
  } = content;
  const contentTypeId = getContentTypeId(content);
  const formattedScheduledDate = contentStatusType === 'scheduled' && publishDate ? getDateTime(publishDate, false) : null;
  let statusText = /*#__PURE__*/_jsx(FormattedMessage, {
    "aria-hidden": true,
    "data-test-id": `content-status-${contentStatusType}`,
    message: getStatusLabelKey(contentStatusType),
    options: {
      scheduledDate: formattedScheduledDate
    }
  });
  if (contentStatusType === 'danger' && securityState) {
    statusText = /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        "aria-hidden": true,
        "data-test-id": `content-status-${securityState}`,
        message: getStatusLabelKey(securityState, 'status')
      }), /*#__PURE__*/_jsx(HelpIcon, {
        title: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: getStatusLabelKey(securityState, `tooltip.${contentTypeId === Categories.blog_post.id ? 'posts' : 'pages'}`)
        }),
        tooltipPlacement: "bottom",
        tooltipProps: {
          use: securityState === 'SECURITY_VIOLATION' ? 'longform' : 'default'
        },
        marginInlineStart: "100"
      })]
    });
  }
  const hasAdvancedLPWithoutAccess = contentTypeId === Categories.landing_page.id && !hasAdvancedLandingPagesWriteScope && getIsAdvancedPageTemplate(content);
  if (hasAdvancedLPWithoutAccess) {
    const baseLangKey = getStatusLabelKey(`${contentStatusType}WithoutAccess`);
    return /*#__PURE__*/_jsxs("td", {
      "data-test-id": "content-status-cell",
      children: [/*#__PURE__*/_jsx(WarningIcon, {
        size: "200",
        color: "fill-alert-default"
      }), /*#__PURE__*/_jsxs(StatusTag, {
        use: ContentStatusToUIStatusTagMap[contentStatusType],
        inline: true,
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          "aria-hidden": true,
          "data-test-id": `content-status-${contentStatusType}WithoutAccess`,
          message: `${baseLangKey}.status`,
          options: {
            scheduledDate: formattedScheduledDate
          }
        }), /*#__PURE__*/_jsx(HelpIcon, {
          title: /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: `${baseLangKey}.tooltip_jsx`,
            elements: {
              KnowledgeBaseButton
            },
            options: {
              url: 'https://knowledge.hubspot.com/account/what-happens-when-i-downgrade-from-a-paid-marketing-hub-or-sales-hub-account',
              external: true
            }
          }),
          tooltipPlacement: "bottom",
          tooltipProps: {
            use: 'longform'
          },
          marginInlineStart: "100"
        })]
      })]
    });
  }
  return /*#__PURE__*/_jsx("td", {
    "data-test-id": "content-status-cell",
    children: /*#__PURE__*/_jsx(StatusTag, {
      use: ContentStatusToUIStatusTagMap[contentStatusType],
      "aria-label": contentStatusType,
      inline: true,
      children: statusText
    })
  });
}, (prevProps, nextProps) => prevProps.content === nextProps.content);
const ContentName = /*#__PURE__*/memo(({
  contentName,
  isContentCurrentlyOpenInEditor,
  editUrl
}) => {
  if (isContentCurrentlyOpenInEditor) {
    return /*#__PURE__*/_jsx(FormattedJSXMessage, {
      message: "contentManagementPanel.table.cells.contentNameCurrentlyEditing_jsx",
      options: {
        contentName,
        url: editUrl
      },
      elements: {
        UILink: Link
      }
    });
  }
  return /*#__PURE__*/_jsx(Link, {
    href: editUrl,
    children: contentName
  });
});
export const NameAndUrlCell = /*#__PURE__*/memo(({
  contentName,
  contentUrl,
  isContentCurrentlyOpenInEditor,
  editUrl
}) => {
  return /*#__PURE__*/_jsxs("div", {
    children: [/*#__PURE__*/_jsx(TruncateString, {
      "aria-hidden": true,
      children: /*#__PURE__*/_jsx(ContentName, {
        contentName: contentName,
        isContentCurrentlyOpenInEditor: isContentCurrentlyOpenInEditor,
        editUrl: editUrl
      })
    }), /*#__PURE__*/_jsx(Small, {
      use: "help",
      "aria-hidden": true,
      children: /*#__PURE__*/_jsx(TruncateString, {
        children: contentUrl
      })
    })]
  });
});